import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as XLSX from "xlsx";
import {
  FiUploadCloud,
  FiAlertTriangle,
  FiEdit2,
  FiCheckCircle,
  FiRotateCcw,
} from "react-icons/fi";
import { Bell } from "assets";
import Table from "components/Table";
import Loading from "components/Loading";
import Pagination from "components/Pagination";
import CustomButton from "components/CustomButton";
import StatusBadge from "components/StatusBadge";
import { FormSelects } from "components/FormInputSelect";
import { TOOLBAR_SELECT_STYLES } from "components/toolbarSelectStyles";
import { actionColumnLayout, columnLayout } from "components/tableColumnLayout";
import { API_GET_MAMBOO } from "services";
import useLoginStore from "hooks/useLoginStore";
import {
  MATCH,
  MATCH_LABEL,
  isDspSupported,
  overrideKey,
  parseDspReport,
  rightsForAdjust,
  runDistribution,
} from "utils/distribusiDsp";
import "./distribusiDsp.scss";

const ROLE_SONGWRITER = 3;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((n) => ({
  label: String(n),
  value: n,
}));

const TABS = [
  { key: "impor", label: "Impor Laporan" },
  { key: "pemetaan", label: "Pemetaan" },
  { key: "distribusi", label: "Distribusi" },
  { key: "ringkasan", label: "Ringkasan" },
];

const STATUS_COLOR = {
  [MATCH.MATCHED]: "var(--success)",
  [MATCH.PARTIAL]: "#BF6919",
  [MATCH.CONFLICT]: "var(--danger)",
  [MATCH.UNMATCHED]: "var(--text-muted)",
};

const rupiah = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 2,
});

const formatRp = (value) => rupiah.format(Number(value) || 0);

/** Pecah array jadi satu halaman. Seluruh data ada di memori, tidak ada API. */
const paginate = (rows, page, size) => rows.slice((page - 1) * size, page * size);

/**
 * Footer tabel: pemilih jumlah baris di kiri, nomor halaman di kanan —
 * mengikuti pola `.table-footer` yang sudah dipakai halaman lain.
 */
const TableFooter = ({ total, page, setPage, pageSize, setPageSize }) => (
  <div className="table-footer">
    <div className="table-footer-filters">
      <p className="text-show">Show :</p>
      <FormSelects
        options={PAGE_SIZE_OPTIONS}
        value={pageSize.value}
        onChange={(opt) => {
          setPageSize(opt);
          setPage(1);
        }}
        styles={TOOLBAR_SELECT_STYLES}
      />
    </div>
    <Pagination
      isTotalPage={Math.max(1, Math.ceil(total / pageSize.value))}
      isPage={page}
      handleClickNumber={setPage}
      pageToShow={6}
    />
  </div>
);

/**
 * Laporan > Distribusi — pembacaan laporan DSP dan pemetaannya ke katalog.
 *
 * Tahap ini SENGAJA tidak menyimpan apa pun. Seluruh hasil hidup di memori
 * halaman: begitu di-refresh, datanya hilang. Tujuannya memastikan pemetaannya
 * benar dulu sebelum skema penyimpanan ditentukan.
 *
 * Dua sudut pandang dari data yang sama:
 *  - publisher/admin melihat seluruh aliran distribusi,
 *  - songwriter hanya barisnya sendiri. Pembatasan itu ditegakkan dua lapis:
 *    backend hanya mengirim lagu yang ia punya hak di dalamnya, dan di sini
 *    hasil distribusinya disaring lagi ke `creator_id` miliknya.
 */
const DistribusiDsp = () => {
  const serverProfile = useLoginStore((state) => state.serverProfile);
  const roleCode = serverProfile?.role_code;
  const isSongwriter = roleCode === ROLE_SONGWRITER;

  const fileInputRef = useRef(null);

  const [master, setMaster] = useState(null);
  const [isLoadingMaster, setIsLoadingMaster] = useState(true);
  const [masterError, setMasterError] = useState("");

  // DSP asal laporan. Dipilih SEBELUM berkas, karena tiap DSP punya susunan
  // sheet dan nama kolom sendiri — pembacaannya tidak bisa diasumsikan sama.
  const [dsp, setDsp] = useState(null);

  const [fileName, setFileName] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  // Hasil BACA berkas. Murni frontend, tidak bergantung pada backend sama
  // sekali — berkas tetap bisa dibaca walau katalog belum termuat.
  const [report, setReport] = useState(null);
  const [parseError, setParseError] = useState("");

  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);

  // Penyesuaian porsi, per lagu + pemegang hak + kolom hak. Hanya di memori —
  // belum ada penyimpanan, sesuai tahap ini.
  const [overrides, setOverrides] = useState({});
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustDraft, setAdjustDraft] = useState([]);

  // Persetujuan masih sebatas penanda di layar: belum ada yang dikirim ke
  // songwriter mana pun karena backend-nya memang belum dibuat.
  const [isApproved, setIsApproved] = useState(false);

  // Publisher bisa meninjau tampilan seorang songwriter tanpa berganti akun.
  // Tanpa ini sisi songwriter mustahil dicoba: datanya hanya ada selama
  // berkasnya baru saja diunggah, dan songwriter tidak mengunggah laporan DSP.
  const [viewAsCreator, setViewAsCreator] = useState(null);

  const loadMaster = useCallback(() => {
    setIsLoadingMaster(true);
    return API_GET_MAMBOO.dynamic
      .getDistributionMaster()
      .then((data) => {
        // Server yang salah bisa membalas 200 dengan isi yang bukan JSON kita
        // (mis. dev server lain yang kebetulan memakai port backend). Bentuknya
        // diperiksa, bukan hanya status HTTP-nya.
        if (!data || !Array.isArray(data.songs)) {
          throw new Error(
            "Respons tidak dikenali. Pastikan REACT_APP_API_URL_SERVER menunjuk ke backend Mamboo dan backend-nya berjalan."
          );
        }
        setMaster(data);
        setMasterError("");
      })
      .catch((err) => {
        setMaster(null);
        setMasterError(
          err?.response?.data?.data ||
            err?.message ||
            "Data katalog gagal dimuat."
        );
      })
      .finally(() => setIsLoadingMaster(false));
  }, []);

  useEffect(() => {
    loadMaster();
  }, [loadMaster]);


  const handlePickFile = () => fileInputRef.current?.click();

  // Berganti DSP membuang hasil baca sebelumnya: berkas lama dibaca dengan
  // aturan format DSP lama, jadi menyimpannya hanya akan menyesatkan.
  const handleChangeDsp = (option) => {
    setDsp(option);
    setReport(null);
    setFileName("");
    setParseError("");
    setActiveTab("impor");
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setIsParsing(true);
    setParseError("");
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const parsed = parseDspReport(workbook, XLSX);

      if (parsed.rows.length === 0) {
        setReport(null);
        setParseError(
          parsed.warnings[0] || "Tidak ada baris data yang bisa dibaca."
        );
        return;
      }

      setReport(parsed);
      setPage(1);
    } catch (err) {
      setReport(null);
      setParseError(`Berkas gagal dibaca: ${err.message}`);
    } finally {
      setIsParsing(false);
    }
  };

  // Pencocokan dihitung ulang kapan pun berkas ATAU katalog berubah. Jadi kalau
  // katalog baru berhasil dimuat setelah berkasnya dibaca, hasilnya langsung
  // muncul tanpa perlu mengunggah ulang.
  const result = useMemo(
    () =>
      report && master
        ? runDistribution(report.rows, master, {
            dspName: dsp?.value,
            overrides,
          })
        : null,
    [report, master, dsp, overrides]
  );
  // Begitu hasil pencocokan tersedia, langsung bawa ke tab Pemetaan — baik
  // karena berkasnya baru dibaca maupun karena katalognya baru berhasil dimuat.
  useEffect(() => {
    if (result) setActiveTab((prev) => (prev === "impor" ? "pemetaan" : prev));
  }, [result]);

  // Satu titik penyaringan peran: id yang dipakai untuk membatasi hasil.
  const limitToCreatorId = isSongwriter
    ? master?.self?.creator_id || null
    : viewAsCreator?.value || null;

  const distributions = useMemo(() => {
    if (!result) return [];
    if (!limitToCreatorId) return result.distributions;
    return result.distributions.filter((d) => d.creatorId === limitToCreatorId);
  }, [result, limitToCreatorId]);

  const perCreator = useMemo(() => {
    if (!result) return [];
    if (!limitToCreatorId) return result.perCreator;
    return result.perCreator.filter((c) => c.creatorId === limitToCreatorId);
  }, [result, limitToCreatorId]);

  const matchedRows = useMemo(() => {
    if (!result) return [];
    const rows = statusFilter?.value
      ? result.matchedRows.filter((r) => r.status === statusFilter.value)
      : result.matchedRows;
    if (!limitToCreatorId) return rows;
    // Untuk songwriter, baris laporan yang relevan adalah yang lagunya memuat
    // haknya — ditentukan dari hasil distribusi, bukan dari kolom Writers.
    const ownRowIds = new Set(distributions.map((d) => d.rowId));
    return rows.filter((r) => ownRowIds.has(r.id));
  }, [result, statusFilter, limitToCreatorId, distributions]);

  /**
   * Pilihan DSP. Diambil dari referensi `rt_dsp` bila katalog sudah termuat;
   * kalau belum, tetap ada satu pilihan YouTube supaya membaca berkas tidak
   * ikut tergantung pada server — hanya pencocokannya yang butuh.
   */
  const dspOptions = useMemo(() => {
    const list = master?.dsp?.length
      ? master.dsp.map((d) => d.dsp)
      : ["Youtube"];
    return list.map((name) => ({
      value: name,
      label: isDspSupported(name) ? name : `${name} (format belum didukung)`,
    }));
  }, [master]);

  // Begitu daftarnya ada, YouTube dipilihkan: satu-satunya yang didukung, jadi
  // tidak ada gunanya memaksa pengguna memilihnya sendiri.
  useEffect(() => {
    if (dsp) return;
    const youtube = dspOptions.find((o) => isDspSupported(o.value));
    if (youtube) setDsp(youtube);
  }, [dspOptions, dsp]);

  const isDspReady = Boolean(dsp) && isDspSupported(dsp.value);

  const creatorOptions = useMemo(
    () =>
      (result?.perCreator || []).map((c) => ({
        label: `${c.creatorName}${c.ipBaseNumber ? ` (${c.ipBaseNumber})` : ""}`,
        value: c.creatorId,
      })),
    [result]
  );

  const openAdjust = (row) => {
    setAdjustTarget({
      songId: row.songId,
      songTitle: row.songTitle,
      column: row.rightColumn,
      rightType: row.rightType,
    });
    setAdjustDraft(
      rightsForAdjust(master, row.songId, row.rightColumn, overrides)
    );
  };

  const closeAdjust = () => {
    setAdjustTarget(null);
    setAdjustDraft([]);
  };

  const changeDraftPct = (creatorId, value) => {
    // Input dibiarkan sebagai teks supaya bisa dikosongkan saat mengetik;
    // konversi angkanya dilakukan di total dan saat disimpan.
    setAdjustDraft((prev) =>
      prev.map((r) =>
        r.creatorId === creatorId ? { ...r, percentage: value } : r
      )
    );
  };

  const draftTotal = adjustDraft.reduce(
    (sum, r) => sum + (Number(r.percentage) || 0),
    0
  );
  const isDraftValid = Math.abs(draftTotal - 100) < 0.01;

  const saveAdjust = () => {
    if (!adjustTarget || !isDraftValid) return;
    setOverrides((prev) => {
      const next = { ...prev };
      adjustDraft.forEach((r) => {
        next[overrideKey(adjustTarget.songId, r.creatorId, adjustTarget.column)] =
          Number(r.percentage) || 0;
      });
      return next;
    });
    // Angka yang sudah disetujui tidak boleh diam-diam berubah sesudahnya.
    setIsApproved(false);
    closeAdjust();
  };

  const resetAdjust = () => {
    if (!adjustTarget) return;
    setOverrides((prev) => {
      const next = { ...prev };
      adjustDraft.forEach((r) => {
        delete next[
          overrideKey(adjustTarget.songId, r.creatorId, adjustTarget.column)
        ];
      });
      return next;
    });
    setIsApproved(false);
    closeAdjust();
  };

  const changeTab = useCallback((key) => {
    setActiveTab(key);
    setPage(1);
  }, []);

  // ── Kolom tabel ──────────────────────────────────────────────────────────

  const stageBadge = (ok, label) => (
    <span className={`stage-pill ${ok ? "is-ok" : "is-off"}`}>{label}</span>
  );

  const mappingColumns = [
    {
      dataField: "assetTitle",
      text: "Judul di Laporan",
      headerStyle: columnLayout(1.6),
      style: columnLayout(1.6),
    },
    {
      dataField: "customId",
      text: "Custom ID",
      headerStyle: columnLayout(1),
      style: columnLayout(1),
    },
    {
      dataField: "song",
      text: "Lagu di Katalog",
      formatter: (cell, row) =>
        row.song ? (
          row.song.song_title
        ) : (
          <span style={{ color: "var(--text-muted)" }}>-</span>
        ),
      headerStyle: columnLayout(1.6),
      style: columnLayout(1.6),
    },
    {
      dataField: "stages",
      isDummyField: true,
      // Hanya dua penanda ini yang menentukan status di kolom sebelah. Nama
      // penulis tidak ikut, jadi tidak ditampilkan di sini — ketidakcocokannya
      // dilaporkan sekali sebagai peringatan di atas tabel.
      text: "Aset / Work ID",
      formatExtraData: matchedRows,
      formatter: (cell, row) => (
        <div className="stage-group">
          {stageBadge(row.stage1.ok, "Aset")}
          {stageBadge(row.stage3.ok, "Work ID")}
        </div>
      ),
      headerStyle: columnLayout(1.1, "center"),
      style: columnLayout(1.1, "center"),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) => (
        <StatusBadge
          title={MATCH_LABEL[cell] || cell}
          customStyle={{
            border: `1px solid ${STATUS_COLOR[cell]}`,
            color: STATUS_COLOR[cell],
            flex: "0 0 auto",
            width: "fit-content",
          }}
        />
      ),
      headerStyle: columnLayout(1.2, "center"),
      style: columnLayout(1.2, "center"),
    },
    {
      dataField: "incomeRev",
      text: "Income Rev",
      formatter: (cell) => formatRp(cell),
      headerStyle: columnLayout(1.1, "center"),
      style: columnLayout(1.1, "center"),
    },
  ];

  const distribusiColumns = [
    {
      dataField: "songTitle",
      text: "Lagu",
      headerStyle: columnLayout(1.3),
      style: columnLayout(1.3),
    },
    {
      dataField: "creatorName",
      text: "Pemegang Hak",
      // Paling lebar di tabel ini: namanya berawalan IP base/Mamboo ID, mis.
      // "I-005719967-2 - Saritomo Agung Widayat". Dengan porsi yang terlalu
      // kecil, namanya terputus di tengah angka dan barisnya jadi empat baris.
      headerStyle: columnLayout(2.4),
      style: columnLayout(2.4),
    },
    {
      dataField: "rightType",
      text: "Jenis Hak",
      headerStyle: columnLayout(0.9),
      style: columnLayout(0.9),
    },
    {
      dataField: "percentage",
      text: "Porsi",
      formatExtraData: overrides,
      formatter: (cell, row) => (
        <span className={row.isAdjusted ? "pct-adjusted" : ""}>
          {cell}%
          {row.isAdjusted && (
            <small title={`Semula ${row.originalPercentage}%`}>
              {" "}
              (dari {row.originalPercentage}%)
            </small>
          )}
        </span>
      ),
      headerStyle: columnLayout(1, "center"),
      style: columnLayout(1, "center"),
    },
    {
      dataField: "sourceRev",
      text: "Income Rev",
      formatter: (cell) => formatRp(cell),
      headerStyle: columnLayout(1.1, "center"),
      style: columnLayout(1.1, "center"),
    },
    {
      dataField: "amount",
      text: "Diterima",
      formatter: (cell) => <b>{formatRp(cell)}</b>,
      headerStyle: columnLayout(1.2, "center"),
      style: columnLayout(1.2, "center"),
    },
    {
      dataField: "adjust",
      isDummyField: true,
      text: "Atur",
      // Tanpa formatExtraData, sel dummy tidak pernah dirender ulang dan
      // tombolnya memegang baris versi lama (lihat cell.js di
      // react-bootstrap-table-next).
      formatExtraData: overrides,
      formatter: (cell, row) => (
        <button
          type="button"
          className="dsp-adjust-btn"
          title="Atur porsi pembagian lagu ini"
          aria-label={`Atur porsi ${row.songTitle}`}
          disabled={!master}
          onClick={() => openAdjust(row)}
        >
          <FiEdit2 />
        </button>
      ),
      headerStyle: actionColumnLayout(80),
      style: actionColumnLayout(80),
    },
  ];

  const creatorColumns = [
    {
      dataField: "creatorName",
      text: "Pemegang Hak",
      headerStyle: columnLayout(2),
      style: columnLayout(2),
    },
    {
      dataField: "ipBaseNumber",
      text: "IP Base",
      formatter: (cell) => cell || "-",
      headerStyle: columnLayout(1.2),
      style: columnLayout(1.2),
    },
    {
      dataField: "songCount",
      text: "Jumlah Lagu",
      headerStyle: columnLayout(0.9, "center"),
      style: columnLayout(0.9, "center"),
    },
    {
      dataField: "rows",
      text: "Baris",
      headerStyle: columnLayout(0.8, "center"),
      style: columnLayout(0.8, "center"),
    },
    {
      dataField: "amount",
      text: "Total Diterima",
      formatter: (cell) => <b>{formatRp(cell)}</b>,
      headerStyle: columnLayout(1.4, "center"),
      style: columnLayout(1.4, "center"),
    },
  ];

  /**
   * Baris yang dibentangkan di tabel per pemegang hak: dari lagu mana saja
   * totalnya berasal. Memakai `expandRow` bawaan tabel, pola yang sudah dipakai
   * halaman laporan lain.
   */
  const creatorExpand = {
    onlyOneExpanding: true,
    showExpandColumn: true,
    renderer: (row) => (
      <div className="dsp-breakdown">
        <div className="dsp-breakdown-head">
          <span>Lagu</span>
          <span>Porsi</span>
          <span>Baris</span>
          <span>Total</span>
        </div>
        {(row.bySong || []).map((sg) => (
          <div className="dsp-breakdown-row" key={sg.songId}>
            <span>
              {sg.songTitle}
              {sg.workId && <small> · {sg.workId}</small>}
            </span>
            {/* `data-label` dipakai CSS untuk memberi keterangan saat layar
                sempit, di mana baris judul kolomnya disembunyikan. */}
            <span
              data-label="Porsi"
              className={sg.isAdjusted ? "pct-adjusted" : ""}
            >
              {sg.percentage}%
            </span>
            <span data-label="Baris">{sg.rows.toLocaleString("id-ID")}</span>
            <span data-label="Total">
              <b>{formatRp(sg.amount)}</b>
            </span>
          </div>
        ))}
      </div>
    ),
  };

  // ── Isi tiap tab ─────────────────────────────────────────────────────────

  const renderImpor = () => (
    <>
      <div className="dsp-upload-card">
        <FiUploadCloud />
        <h3>Unggah laporan DSP</h3>
        <p>
          Pilih DSP asal laporannya dulu, lalu unggah berkas Excel-nya. Untuk
          YouTube, sheet yang dibaca: ADS REV, SUBS REV, dan ADJ SUBS REV; sheet
          REKAP dipakai sebagai pembanding total, dan dasar perhitungannya kolom{" "}
          <b>Income Rev</b>.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFile}
        />
        <div className="dsp-source">
          <label htmlFor="dsp-sumber">Laporan dari DSP</label>
          {/* Tanpa `styles`: TOOLBAR_SELECT_STYLES mematok lebar 75px karena
              dibuat untuk pemilih "Show : 25". Tema global `.app-select__*`
              sudah memberi tampilan form select yang benar. */}
          <FormSelects
            id="dsp-sumber"
            placeholder="Pilih DSP"
            options={dspOptions}
            value={dsp?.value}
            onChange={handleChangeDsp}
          />
        </div>

        {/* Tidak bergantung pada backend: membaca Excel murni di browser. */}
        <CustomButton
          label={isParsing ? "Membaca…" : "Pilih Berkas"}
          color="primary-btn"
          disabled={isParsing || !isDspReady}
          onClick={handlePickFile}
        />
        {fileName && <span className="dsp-file-name">{fileName}</span>}

        {dsp && !isDspSupported(dsp.value) && (
          <div className="dsp-blocked">
            <p>
              Format laporan <b>{dsp.value}</b> belum bisa dibaca. Untuk sekarang
              baru YouTube. Susunan sheet dan nama kolom tiap DSP berbeda, dan
              membacanya dengan aturan YouTube akan menghasilkan angka yang
              salah tanpa terlihat salah.
            </p>
          </div>
        )}
        {/* Katalog hanya dibutuhkan untuk MENCOCOKKAN, bukan untuk membaca.
            Jadi kegagalannya dijelaskan di sini tanpa mengunci tombol unggah. */}
        {!master && (
          <div className="dsp-blocked">
            <p>
              Berkas tetap bisa dibaca, tapi pencocokan ke katalog belum bisa
              dijalankan: data lagu & pencipta gagal diambil dari server.
            </p>
            <CustomButton
              label="Coba Muat Ulang Katalog"
              className="button-filter"
              onClick={loadMaster}
            />
          </div>
        )}
      </div>

      {parseError && (
        <div className="dsp-notice is-error" role="alert">
          <FiAlertTriangle /> <span>{parseError}</span>
        </div>
      )}

      {report && (
        <section className="dsp-card">
          <h3>Isi berkas</h3>
          <Table
            columnRefs={[
              {
                dataField: "name",
                text: "Sheet",
                headerStyle: columnLayout(1.6),
                style: columnLayout(1.6),
              },
              {
                dataField: "label",
                text: "Jenis",
                headerStyle: columnLayout(1.4),
                style: columnLayout(1.4),
              },
              {
                dataField: "rowCount",
                text: "Baris",
                headerStyle: columnLayout(0.8, "center"),
                style: columnLayout(0.8, "center"),
              },
              {
                dataField: "declaredTotal",
                text: "Total di Berkas",
                formatter: (cell) => (cell === null ? "-" : formatRp(cell)),
                headerStyle: columnLayout(1.3, "center"),
                style: columnLayout(1.3, "center"),
              },
              {
                dataField: "computedTotal",
                text: "Jumlah Baris Terbaca",
                formatter: (cell) => formatRp(cell),
                headerStyle: columnLayout(1.3, "center"),
                style: columnLayout(1.3, "center"),
              },
            ]}
            dataTable={report.sheets.map((s, i) => ({ ...s, id: i }))}
            noDataText="Tidak ada sheet data"
          />
          <p className="dsp-hint">
            Baris TOTAL di akhir tiap sheet tidak ikut dibaca sebagai data.
            Nilainya dipakai sebagai pembanding pada kolom "Total di Berkas".
          </p>
        </section>
      )}
    </>
  );

  const renderStatTiles = () => {
    if (!result) return null;
    const { summary, reconciliation } = result;
    const tiles = [
      { label: "Baris Laporan", value: summary.total.toLocaleString("id-ID") },
      { label: "Cocok", value: summary.byStatus.matched.toLocaleString("id-ID") },
      {
        label: "Cocok Sebagian",
        value: summary.byStatus.partial.toLocaleString("id-ID"),
      },
      {
        label: "Tidak Cocok / Konflik",
        value: (
          summary.byStatus.unmatched + summary.byStatus.conflict
        ).toLocaleString("id-ID"),
      },
      { label: "Total Sumber", value: formatRp(reconciliation.totalSource) },
    ];
    return (
      <div className="dsp-tiles">
        {tiles.map((t) => (
          <div className="dsp-tile" key={t.label}>
            <span className="dsp-tile-label">{t.label}</span>
            <span className="dsp-tile-value">{t.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderPemetaan = () => {
    const rows = paginate(matchedRows, page, pageSize.value).map((r) => ({
      ...r,
      id: r.id,
    }));
    return (
      <>
        {renderStatTiles()}
        {(result.summary.unknownAssetCount > 0 ||
          result.summary.writerMismatch > 0) && (
          <div className="dsp-notice is-warning">
            <FiAlertTriangle />
            <span>
              {result.summary.unknownAssetCount.toLocaleString("id-ID")} Asset ID
              di laporan belum terdaftar di katalog, dan{" "}
              {result.summary.unknownWorkIdCount.toLocaleString("id-ID")} Custom
              ID belum cocok dengan Work ID mana pun. Baris yang masih dikenali
              lewat salah satunya tetap diproses dan ditandai{" "}
              <b>Cocok Sebagian</b>.
              {result.summary.writerMismatch > 0 && (
                <>
                  {" "}
                  Selain itu{" "}
                  {result.summary.writerMismatch.toLocaleString("id-ID")} baris
                  punya nama penulis yang tidak ada di komposisi lagunya. Ini
                  tidak memblokir pembagian, tapi patut diperiksa karena bisa
                  berarti Asset ID salah pasang.
                </>
              )}
            </span>
          </div>
        )}
        <section className="dsp-card">
          <div className="dsp-card-toolbar">
            <h3>Pemetaan per baris laporan</h3>
            <div className="dsp-filter">
              <FormSelects
                placeholder="Semua status"
                isClearable
                options={Object.values(MATCH).map((v) => ({
                  label: MATCH_LABEL[v],
                  value: v,
                }))}
                value={statusFilter?.value}
                onChange={(val) => {
                  setStatusFilter(val);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <Table
            columnRefs={mappingColumns}
            dataTable={rows}
            noDataText="Tidak ada baris"
          />
          <TableFooter
            total={matchedRows.length}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </section>
      </>
    );
  };

  const renderDistribusi = () => {
    const rows = paginate(distributions, page, pageSize.value).map((d, i) => ({
      ...d,
      id: `${d.rowId}-${d.creatorId}-${i}`,
    }));
    return (
      <section className="dsp-card">
        <div className="dsp-card-toolbar">
          <h3>
            Hasil distribusi{" "}
            <span className="dsp-count">
              {distributions.length.toLocaleString("id-ID")} baris
            </span>
          </h3>
        </div>
        <Table
          columnRefs={distribusiColumns}
          dataTable={rows}
          noDataText="Belum ada hasil distribusi"
        />
        <TableFooter
          total={distributions.length}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </section>
    );
  };

  const renderRingkasan = () => {
    const r = result.reconciliation;
    const baris = [
      { k: "Total pendapatan sumber", v: r.totalSource, strong: true },
      { k: "Dibagikan ke pemegang hak", v: r.totalDistributed, strong: true },
      { k: "Tertahan: tidak cocok / konflik", v: r.totalUnmatched },
      { k: "Tertahan: pengecualian komposisi", v: r.totalException },
      { k: "Selisih rekonsiliasi", v: r.difference, diff: true },
      { k: "Selisih pembulatan per baris", v: r.roundingDrift, muted: true },
    ];

    return (
      <>
        {/* Persetujuan. Masih sebatas penanda di layar — pengirimannya ke tiap
            songwriter menunggu backend, dan itu dinyatakan apa adanya supaya
            tidak terkira sudah terkirim. */}
        {!isSongwriter && (
          <section className="dsp-card dsp-approval">
            <div className="dsp-card-toolbar">
              <h3>Persetujuan distribusi</h3>
              {isApproved ? (
                <div className="dsp-approved">
                  <FiCheckCircle />
                  <span>Disetujui di layar ini</span>
                  <button
                    type="button"
                    className="dsp-adjust-btn"
                    title="Batalkan persetujuan"
                    aria-label="Batalkan persetujuan"
                    onClick={() => setIsApproved(false)}
                  >
                    <FiRotateCcw />
                  </button>
                </div>
              ) : (
                <CustomButton
                  label="Setujui Distribusi"
                  color="primary-btn"
                  disabled={!r.isBalanced || distributions.length === 0}
                  onClick={() => setIsApproved(true)}
                />
              )}
            </div>
            <p className="dsp-hint">
              {isApproved
                ? "Belum ada apa pun yang dikirim ke songwriter: penyimpanan dan pengirimannya menyusul bersama backend. Mengubah porsi akan membatalkan persetujuan ini."
                : !r.isBalanced
                  ? "Belum bisa disetujui: rekonsiliasinya belum seimbang, masih ada pendapatan yang belum terjelaskan."
                  : "Menyetujui akan mengunci angka di halaman ini. Pengiriman ke masing-masing songwriter menyusul bersama backend."}
            </p>
          </section>
        )}

        <section className="dsp-card">
          <h3>Rekonsiliasi</h3>
          <ul className="dsp-recon">
            {baris.map((b) => (
              <li key={b.k} className={b.muted ? "is-muted" : ""}>
                <span>{b.k}</span>
                <span
                  style={{
                    fontWeight: b.strong ? 700 : 500,
                    color: b.diff
                      ? r.isBalanced
                        ? "var(--success)"
                        : "var(--danger)"
                      : undefined,
                  }}
                >
                  {formatRp(b.v)}
                </span>
              </li>
            ))}
          </ul>
          <p className="dsp-hint">
            {r.isBalanced
              ? "Seluruh pendapatan sumber terjelaskan. Selisih pembulatan dilaporkan terpisah dan bukan pendapatan yang hilang."
              : "Masih ada pendapatan yang belum terjelaskan. Selisih ini sengaja tidak dipaksa nol."}
          </p>
        </section>

        {result.exceptions.length > 0 && (
          <section className="dsp-card">
            <h3>
              Pengecualian{" "}
              <span className="dsp-count">{result.exceptions.length}</span>
            </h3>
            <Table
              columnRefs={[
                {
                  dataField: "songTitle",
                  text: "Lagu",
                  formatter: (cell) => cell || "-",
                  headerStyle: columnLayout(1.6),
                  style: columnLayout(1.6),
                },
                {
                  dataField: "reason",
                  text: "Alasan",
                  headerStyle: columnLayout(2.6),
                  style: columnLayout(2.6),
                },
                {
                  dataField: "amount",
                  text: "Nilai Tertahan",
                  formatter: (cell) => formatRp(cell),
                  headerStyle: columnLayout(1.2, "center"),
                  style: columnLayout(1.2, "center"),
                },
              ]}
              dataTable={result.exceptions
                .slice(0, 100)
                .map((e, i) => ({ ...e, id: i }))}
              noDataText="Tidak ada pengecualian"
            />
          </section>
        )}

        <section className="dsp-card">
          <div className="dsp-card-toolbar">
            <h3>Per pemegang hak</h3>
            {!isSongwriter && creatorOptions.length > 0 && (
              <div className="dsp-filter">
                <FormSelects
                  placeholder="Lihat sebagai songwriter…"
                  isClearable
                  options={creatorOptions}
                  value={viewAsCreator?.value}
                  onChange={setViewAsCreator}
                />
              </div>
            )}
          </div>
          <Table
            columnRefs={creatorColumns}
            dataTable={perCreator.map((c) => ({ ...c, id: c.key }))}
            noDataText="Belum ada hasil"
            expandRow={creatorExpand}
          />
          <p className="dsp-hint">
            Klik satu baris untuk melihat dari lagu mana saja totalnya berasal.
          </p>
        </section>
      </>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (isLoadingMaster) return <Loading />;

  return (
    <Container fluid className="content-pencipta distribusi-dsp">
      <div className="header-content">
        <div className="pencipta-header">
          <h1>Distribusi (under dev)</h1>
          <span>
            {isSongwriter
              ? "Pemetaan laporan pendapatan DSP ke karya Anda. Tahap ini belum menyimpan data: hasilnya hanya tampil selama berkas baru diunggah."
              : "Baca laporan pendapatan DSP, cocokkan ke katalog lagu, lalu lihat pembagiannya per pemegang hak. Tahap ini belum menyimpan apa pun ke database."}
          </span>
        </div>
        <div className="icon-wrapper">
          <Bell />
        </div>
      </div>

      {masterError && (
        <div className="dsp-notice is-error" role="alert">
          <FiAlertTriangle /> <span>{masterError}</span>
        </div>
      )}

      <div className="dsp-tabs" role="tablist">
        {TABS.map((tab) => {
          // Tab selain Impor butuh hasil pencocokan — yaitu berkas DAN katalog.
          const disabled = tab.key !== "impor" && !result;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`dsp-tab ${activeTab === tab.key ? "is-active" : ""}`}
              disabled={disabled}
              title={
                disabled
                  ? !report
                    ? "Unggah laporan dulu"
                    : "Menunggu data katalog dari server"
                  : undefined
              }
              onClick={() => changeTab(tab.key)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <Modal
        isOpen={Boolean(adjustTarget)}
        toggle={closeAdjust}
        centered
        className="dsp-adjust-modal"
      >
        <ModalHeader toggle={closeAdjust}>Atur Porsi Pembagian</ModalHeader>
        <ModalBody>
          <p className="dsp-hint" style={{ marginTop: 0 }}>
            <b>{adjustTarget?.songTitle}</b>, jenis hak{" "}
            {adjustTarget?.rightType}. Perubahan berlaku untuk{" "}
            <b>seluruh baris laporan lagu ini</b>, karena kesepakatan pembagian
            memang per lagu, bukan per baris harian.
          </p>
          <div className="dsp-adjust-list">
            {adjustDraft.map((r) => (
              <label key={r.creatorId} className="dsp-adjust-row">
                <span>
                  {r.creatorName}
                  {r.original !== Number(r.percentage) && (
                    <small> · semula {r.original}%</small>
                  )}
                </span>
                <span className="dsp-adjust-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={r.percentage}
                    onChange={(e) => changeDraftPct(r.creatorId, e.target.value)}
                  />
                  <i>%</i>
                </span>
              </label>
            ))}
          </div>
          <div
            className={`dsp-adjust-total ${isDraftValid ? "is-ok" : "is-off"}`}
          >
            <span>Total</span>
            <span>{Math.round(draftTotal * 100) / 100}%</span>
          </div>
          {!isDraftValid && (
            <p className="dsp-adjust-warn" role="alert">
              Total porsi harus tepat 100%. Kalau tidak, lagu ini akan tertahan
              sebagai pengecualian dan pendapatannya tidak dibagikan.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          {/* Satu kelas untuk keduanya: `button-filter` dan `primary-btn`
              berasal dari keluarga gaya berbeda, jadi tinggi dan paddingnya
              tidak pernah sama. */}
          <CustomButton
            label="Kembalikan ke Awal"
            className="dsp-modal-btn is-secondary"
            color=""
            ukuran=""
            onClick={resetAdjust}
          />
          <CustomButton
            label="Simpan Porsi"
            className="dsp-modal-btn is-primary"
            color=""
            ukuran=""
            disabled={!isDraftValid}
            onClick={saveAdjust}
          />
        </ModalFooter>
      </Modal>

      {activeTab === "impor" && renderImpor()}
      {activeTab === "pemetaan" && result && renderPemetaan()}
      {activeTab === "distribusi" && result && renderDistribusi()}
      {activeTab === "ringkasan" && result && renderRingkasan()}
    </Container>
  );
};

export default DistribusiDsp;
